import React, { ReactNode, useEffect, useState } from "react";
import {
  useLocalVideo,
  useLogger,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import { useDevices } from "@platform/features/video/providers/DevicesProvider";
import { Video, VideoOff } from "lucide-react";
import { isOptionActive } from "@platform/features/video/utils/device-utils";
import { DropdownButtonItem } from "@themes/pollinator/ui/DropdownButtonItem";
import { ButtonDropdownCombo } from "@themes/pollinator/ui/ButtonDropdownCombo";
import { ContainerBreakpoints } from "../../videoMeeting.types";
import { videoEventBus } from "@core/features/video";
import { useBrowserCompatibilityFeatures } from "@platform/features/video/hooks/useBrowserCompatibilityFeatures";

interface Props {
  /** The label that will be shown for video input control, it defaults to `Video`. */
  label?: string;
  containerBreakpoints: ContainerBreakpoints;
}

export const VideoInputControl: React.FC<React.PropsWithChildren<Props>> = ({
  label = "Video",
  containerBreakpoints,
  ...rest
}) => {
  const logger = useLogger();
  const meetingManager = useMeetingManager();
  const { 
    videoInputDevices: devices, 
    selectedVideoInputDevice: selectedDevice,
    selectVideoInputDevice
  } = useDevices();
  const { isVideoEnabled, toggleVideo } = useLocalVideo();
  const [dropdownOptions, setDropdownOptions] = useState<ReactNode[]>([]);
  
  // Get browser compatibility features
  const { supportsVideo } = useBrowserCompatibilityFeatures();


  useEffect(() => {
    const handleClick = async (deviceId: string): Promise<void> => {
      const previousDeviceId = selectedDevice;
      try {
        // Update our custom device provider
        await selectVideoInputDevice(deviceId);

        // Also update the Chime SDK
        if (isVideoEnabled) {
          await meetingManager.startVideoInputDevice(deviceId);
        } else {
          meetingManager.selectVideoInputDevice(deviceId);
        }

        videoEventBus.emitEvent("videochat:video:source:selected", { deviceId });
      } catch (error) {
        logger.error("VideoInputControl failed to select video input device");
        // Rollback: if the new device failed to start (e.g. blank tile),
        // restore the previous device so the user doesn't end up with no video.
        if (previousDeviceId && previousDeviceId !== deviceId) {
          try {
            await selectVideoInputDevice(previousDeviceId);
            if (isVideoEnabled) {
              await meetingManager.startVideoInputDevice(previousDeviceId);
            }
          } catch {
            logger.error("VideoInputControl rollback to previous device also failed");
          }
        }
      }
    };

    const getDropdownOptions = async (): Promise<void> => {
      const dropdownOptions = await Promise.all(
        devices.map(async (device) => (
          <DropdownButtonItem
            key={device.deviceId}
            checked={await isOptionActive(selectedDevice, device.deviceId)}
            onClick={async () => await handleClick(device.deviceId)}
          >
            <span>{device.label}</span>
          </DropdownButtonItem>
        ))
      );
      setDropdownOptions(dropdownOptions);
    };

    getDropdownOptions();
  }, [
    devices,
    selectedDevice,
    isVideoEnabled,
    meetingManager,
    meetingManager.startVideoInputDevice,
    selectVideoInputDevice,
  ]);

  const handleToggleVideo = async () => {
    try {
      if (isVideoEnabled) {
        videoEventBus.emitEvent("videochat:camera:off", {});
      } else {
        videoEventBus.emitEvent("videochat:camera:on", {});
      }
      await toggleVideo();
    } catch (error) {
      logger.error(`VideoInputControl failed to toggle video: ${error}`);
    }
  };

  return (
    <div
      role="group"
      aria-label={isVideoEnabled ? "Camera: on. Click to turn off." : "Camera: off. Click to turn on."}
    >
      <ButtonDropdownCombo
        buttonIcon={isVideoEnabled ? Video : VideoOff}
        buttonLabel={
          containerBreakpoints && containerBreakpoints.isMd ? label : ""
        }
        buttonOnClick={supportsVideo ? handleToggleVideo : () => {}}
        dropdownOptions={supportsVideo ? (dropdownOptions || []) : []}
        destructive={!isVideoEnabled}
        disabled={!supportsVideo}
        buttonAriaPressed={!isVideoEnabled}
        {...rest}
      />
    </div>
  );
};

export default VideoInputControl;
